import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type { NetworkPointSurrenderSettings } from 'src/apis/surrender-service/types';
import type { SurrenderRootRequest } from 'src/apis/surrender-service/use-surrender-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  surrenderRoot: SurrenderRootRequest;
  networkPointId: Uuid | undefined;
};

type ExecuteProps = {
  url: string;
};

export function useNetworkPointSurrenderSettings({
  surrenderRoot,
  networkPointId,
}: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<NetworkPointSurrenderSettings>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!surrenderRoot.response || !networkPointId) return;
    if (!surrenderRoot.response.data._links.getNetworkPointSurrenderSettings)
      throw new Forbidden();

    const templatedUrl =
      surrenderRoot.response.data._links.getNetworkPointSurrenderSettings.href;
    const url = urlTemplate.parse(templatedUrl).expand({ networkPointId });

    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [surrenderRoot.response, networkPointId]);

  if (!surrenderRoot.response || !networkPointId) return;
  if (!surrenderRoot.response.data._links.getNetworkPointSurrenderSettings)
    throw new Forbidden();

  return request;
}

export type NetworkPointSurrenderSettingsRequest = ReturnType<
  typeof useNetworkPointSurrenderSettings
>;
